/* ═══════════════════════════════════════════════════════════════
 * BINANCE — historische data (REST) + realtime data (WebSocket)
 * Client-side, geen backend. Gecombineerde stream: kline + aggTrade.
 * 1:1 poort van TERMINAL.binance uit terminal_wyckoffpcg.html.
 * ═══════════════════════════════════════════════════════════════ */

import { CONFIG as CFG } from "./config";

// ── Historische candles ophalen ─────────────────────────────────
export async function fetchKlines(symbol, interval, limit = CFG.klineLimit) {
  const MAX = 1000; // Binance limiteert één request hard op 1000
  const mapRow = (k) => ({
    time: Math.floor(k[0] / 1000), // Lightweight Charts wil seconden
    open: parseFloat(k[1]),
    high: parseFloat(k[2]),
    low: parseFloat(k[3]),
    close: parseFloat(k[4]),
    volume: parseFloat(k[5]),
  });

  if (limit <= MAX) {
    const res = await fetch(`${CFG.binance.rest}?symbol=${symbol}&interval=${interval}&limit=${limit}`);
    if (!res.ok) throw new Error(`Binance REST ${res.status}`);
    return (await res.json()).map(mapRow);
  }

  // Paginering: telkens ~1000 oudere candles ophalen via endTime en vooraan plakken
  const out = [];
  let endTime = null;
  while (out.length < limit) {
    const need = Math.min(MAX, limit - out.length);
    let url = `${CFG.binance.rest}?symbol=${symbol}&interval=${interval}&limit=${need}`;
    if (endTime != null) url += `&endTime=${endTime}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Binance REST ${res.status}`);
    const raw = await res.json();
    if (!raw.length) break;
    out.unshift(...raw.map(mapRow));
    if (raw.length < need) break;
    endTime = raw[0][0] - 1;
  }

  const seen = new Set(), dedup = [];
  for (const c of out) if (!seen.has(c.time)) { seen.add(c.time); dedup.push(c); }
  dedup.sort((a, b) => a.time - b.time);
  return dedup;
}

// ── Daily levels (PDH / PDL / dag-open) ─────────────────────────
export async function fetchDailyLevels(symbol) {
  const url = `${CFG.binance.rest}?symbol=${symbol}&interval=1d&limit=3`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance daily ${res.status}`);
  const raw = await res.json();
  if (raw.length < 2) return null;
  const prev = raw[raw.length - 2];
  const today = raw[raw.length - 1];
  return {
    pdh: parseFloat(prev[2]),
    pdl: parseFloat(prev[3]),
    dayOpen: parseFloat(today[1]),
  };
}

// ── Order book snapshot (voor de heatmap) ───────────────────────
export async function fetchDepth(symbol, limit = 500) {
  const url = `https://api.binance.com/api/v3/depth?symbol=${symbol}&limit=${limit}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance depth ${res.status}`);
  const d = await res.json();
  return {
    bids: d.bids.map((x) => [parseFloat(x[0]), parseFloat(x[1])]),
    asks: d.asks.map((x) => [parseFloat(x[0]), parseFloat(x[1])]),
  };
}

// ── WebSocket stream manager ────────────────────────────────────
// Beheert 1 gecombineerde verbinding. Bij symbool/timeframe-wissel
// sluiten we netjes af en openen we opnieuw.
export class BinanceStream {
  constructor() {
    this.ws = null;
    this.handlers = {};
    this.closedByUser = false;
    this.reconnectTimer = null;
  }

  connect(symbol, interval, handlers) {
    this.close();
    this.closedByUser = false;
    this.handlers = handlers || {};
    this.symbol = symbol;
    this.interval = interval;

    const s = symbol.toLowerCase();
    const streams = [`${s}@kline_${interval}`, `${s}@aggTrade`].join("/");
    const url = CFG.binance.wsBase + streams;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => { if (this.handlers.onStatus) this.handlers.onStatus("live"); };

    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      const d = msg.data;
      if (!d) return;

      if (d.e === "kline" && this.handlers.onKline) {
        const k = d.k;
        this.handlers.onKline({
          time: Math.floor(k.t / 1000),
          open: parseFloat(k.o),
          high: parseFloat(k.h),
          low: parseFloat(k.l),
          close: parseFloat(k.c),
          volume: parseFloat(k.v),
          closed: k.x === true,
        });
      } else if (d.e === "aggTrade" && this.handlers.onTrade) {
        this.handlers.onTrade({
          price: parseFloat(d.p),
          qty: parseFloat(d.q),
          // m = true -> koper is maker -> agressieve VERKOOP; false -> agressieve KOOP
          isBuy: d.m === false,
          time: d.T,
        });
      }
    };

    ws.onclose = () => {
      if (this.handlers.onStatus) this.handlers.onStatus("offline");
      if (!this.closedByUser) {
        this.reconnectTimer = setTimeout(
          () => this.connect(this.symbol, this.interval, this.handlers), 2000);
      }
    };

    ws.onerror = () => { try { ws.close(); } catch {} };
  }

  close() {
    this.closedByUser = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.ws) { try { this.ws.close(); } catch {} this.ws = null; }
  }
}
